import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useRelayPrice } from '../hooks/useRelayPrice';
import { useTokenChartData, CandleData } from '../hooks/useTokenChartData';
import { formatMarketCap } from '../utils/address';
import CustomSwapWidget from '../components/swap/CustomSwapWidget';
import TokenComments from '../components/token/TokenComments';
import TokenChart from '../components/charts/TokenChart';
import EthNavBar from '../components/eth/EthNavBar';
import EthPriceFooter from '../components/eth/EthPriceFooter';
import { Copy, ExternalLink, ArrowUpRight, ArrowDownRight, Globe, Send, Twitter, Activity, Shield, X } from 'lucide-react';
import { sanitizeUrl } from '../utils/sanitizeUrl';

// Ethereum Brand Colors
const ETH_BLUE = '#627EEA';
const ETH_DARK = '#333333';
const ETH_LIGHT = '#FFFFFF';
const ETH_BLACK = '#000000';

const GECKOTERMINAL_API = 'https://api.geckoterminal.com/api/v2';
const ETH_NETWORK = 'eth';

// Ethereum-styled components
const TerminalContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background: ${ETH_LIGHT};
  color: ${ETH_DARK};
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
`;

const CoverBanner = styled.div`
  position: relative;
  height: 192px;
  width: 100%;
  overflow: hidden;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, ${ETH_BLUE} 0%, #4a69bd 100%);
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.4));
  }
  
  @media (max-width: 768px) {
    height: 160px;
  }
`;

const BannerContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  gap: 16px;
  
  @media (max-width: 768px) {
    padding: 16px;
    gap: 12px;
    flex-wrap: wrap;
  }
`;

const BannerLogo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 4px solid ${ETH_LIGHT};
  background: ${ETH_LIGHT};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

const BannerInfo = styled.div`
  flex: 1;
  padding-bottom: 8px;
`;

const BannerTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${ETH_DARK};
  margin: 0 0 4px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  text-shadow: 0 2px 4px rgba(255, 255, 255, 0.5);
  
  @media (max-width: 768px) {
    font-size: 18px;
    gap: 3px;
  }
`;

const TokenSymbolDisplay = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${ETH_BLUE};
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const TokenNameDisplay = styled.span`
  font-size: 24px;
  font-weight: 800;
  color: ${ETH_DARK};
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const ChainBadge = styled.span`
  font-size: 14px;
  color: #666666;
  font-weight: 500;
  margin-left: 8px;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const BannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 8px;
`;

const SocialButton = styled.a`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
  color: #666666;
  transition: all 0.2s;
  text-decoration: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  &:hover {
    border-color: ${ETH_BLUE};
    background: #FFFFFF;
    color: ${ETH_BLUE};
    transform: translateY(-2px);
  }
`;

const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
`;

const TopBarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  flex-wrap: wrap;
  gap: 8px;
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TokenInfoBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TokenLogoSmall = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const TokenPair = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TokenSymbolBar = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${ETH_DARK};
`;

const TokenPairLabel = styled.span`
  font-size: 12px;
  color: #999999;
`;

const Divider = styled.div`
  width: 1px;
  height: 16px;
  background: rgba(0, 0, 0, 0.1);
`;

const PriceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PriceValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${ETH_BLUE};
`;

const PriceChange = styled.div<{ $positive: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
  background: ${props => props.$positive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  padding: 2px 6px;
  border-radius: 4px;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  
  @media (max-width: 768px) {
    gap: 12px;
    font-size: 11px;
    flex-wrap: wrap;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StatLabel = styled.span`
  color: #999999;
  font-weight: 500;
`;

const StatValue = styled.span`
  font-weight: 600;
  color: ${ETH_DARK};
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  background: #F8F9FA;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    overflow: visible;
  }
`;

const LeftSidebar = styled.div`
  width: 256px;
  border-right: 1px solid rgba(0, 0, 0, 0.1);
  background: #FFFFFF;
  padding: 12px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  @media (max-width: 1024px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    max-height: none;
    overflow-y: visible;
  }
  
  @media (max-width: 768px) {
    padding: 8px;
    gap: 8px;
  }
`;

const TerminalCard = styled.div`
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: #FFFFFF;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 12px;
  background: #FAFAFA;
  border-radius: 8px 8px 0 0;
`;

const CardTitle = styled.h3`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #666666;
  margin: 0;
`;

const CardContent = styled.div`
  padding: 16px;
  
  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const PriceDisplay = styled.div`
  margin-bottom: 16px;
`;

const PriceLabel = styled.p`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #999999;
  margin: 0 0 4px 0;
  font-weight: 600;
`;

const PriceAmount = styled.p`
  font-size: 24px;
  font-weight: 700;
  color: ${ETH_BLUE};
  margin: 0 0 4px 0;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  padding: 6px 0;
`;

const StatRowLabel = styled.span`
  color: #666666;
`;

const StatRowValue = styled.span<{ $highlight?: boolean }>`
  color: ${props => props.$highlight ? ETH_BLUE : ETH_DARK};
  font-weight: ${props => props.$highlight ? '600' : '500'};
  font-family: ${props => props.$highlight ? 'monospace' : 'inherit'};
`;

const DividerLine = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.05);
  margin: 12px 0;
`;

const ContractDisplay = styled.div`
  margin-bottom: 12px;
`;

const ContractAddress = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 8px;
  background: #F5F5F5;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.2s;
  
  &:hover {
    background: #EEEEEE;
    border-color: rgba(0, 0, 0, 0.1);
  }
`;

const ContractText = styled.code`
  font-size: 11px;
  color: #666666;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconButton = styled.button`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #999999;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: ${ETH_BLUE};
  }
`;

const SocialLinksRow = styled.div`
  display: flex;
  gap: 8px;
`;

const SocialLinkSmall = styled.a`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #FFFFFF;
  color: #666666;
  transition: all 0.2s;
  text-decoration: none;
  
  &:hover {
    border-color: ${ETH_BLUE};
    color: ${ETH_BLUE};
    background: #F5F7FF;
  }
`;

const CenterSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #FFFFFF;
`;

const ChartSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  
  iframe {
    min-height: 500px;
    
    @media (max-width: 768px) {
      min-height: 400px;
    }
  }
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const RightSidebar = styled.div`
  width: 400px;
  min-width: 400px;
  border-left: 1px solid rgba(0, 0, 0, 0.1);
  background: #FFFFFF;
  padding: 12px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  @media (max-width: 1024px) {
    width: 100%;
    min-width: 0;
    border-left: none;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    order: -1; /* Show swap widget first on mobile */
  }
  
  @media (max-width: 768px) {
    padding: 8px;
    gap: 8px;
  }
`;

const AboutCard = styled(TerminalCard)`
  margin-top: 12px;
`;

const AboutText = styled.p`
  font-size: 13px;
  line-height: 1.6;
  color: #444444;
  margin: 0;
`;

const AuditBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #666666;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #666666;
  font-size: 16px;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 18px;
`;

const BackButton = styled.button`
  background: #F5F7FF;
  border: 1px solid rgba(98, 126, 234, 0.3);
  border-radius: 8px;
  padding: 10px 20px;
  color: ${ETH_BLUE};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 600;
  
  &:hover {
    background: rgba(98, 126, 234, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 12px;
  }
`;

// Helper function to get pool data from GeckoTerminal
const fetchPoolData = async (poolAddress: string) => {
  try {
    const url = `${GECKOTERMINAL_API}/networks/${ETH_NETWORK}/pools/${poolAddress}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch pool data');
    const data = await response.json();
    return data.data?.attributes;
  } catch (error) {
    console.error('Error fetching pool data:', error);
    return null;
  }
};

// Helper to get pool address from token address
const getPoolAddress = async (tokenAddress: string): Promise<string | null> => {
  try {
    const url = `${GECKOTERMINAL_API}/networks/${ETH_NETWORK}/tokens/${tokenAddress}/pools?page=1`;
    const response = await fetch(url);
    if (!response.ok) return null;
    const data = await response.json();
    if (data.data && data.data.length > 0) {
      return data.data[0].id.split('_')[1];
    }
    return null;
  } catch (error) {
    console.error('Error fetching pool address:', error);
    return null;
  }
};

// Swap Widget Container for Sidebar
const SwapWidgetContainer = styled.div`
  width: 100%;
  padding: 16px;
`;

const EthTokenDetailPage: React.FC = () => {
  const { tokenAddress } = useParams<{ tokenAddress: string }>();
  const navigate = useNavigate();
  const [token, setToken] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [poolData, setPoolData] = useState<any>(null);
  const [poolAddress, setPoolAddress] = useState<string | null>(null);

  const explorerLinkPrefix = 'https://etherscan.io';

  // Get price data for this token
  const { getPriceForToken } = useRelayPrice(token ? [token] : []);
  const priceUSD = token?.contract_address ? getPriceForToken(token.contract_address) : null;

  // Get chart data for calculations (24h change, volume, etc.)
  const { data: chartData } = useTokenChartData(token?.contract_address || null, '1D', ETH_NETWORK);

  // Helper function to safely parse and validate numbers
  const getValidNumber = (val: any): number | null => {
    if (val === null || val === undefined) return null;
    const num = typeof val === 'number' ? val : parseFloat(val);
    if (isNaN(num) || !isFinite(num)) return null;
    return num;
  };

  // Fetch token data and pool info
  useEffect(() => {
    const fetchData = async () => {
      if (!tokenAddress) {
        setError('No token address provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch token from tokens_eth table
        const { data, error: fetchError } = await supabase
          .from('tokens_eth')
          .select('*')
          .eq('contract_address', tokenAddress)
          .single();

        if (fetchError) {
          setError('Token not found');
          setLoading(false);
          return;
        }

        setToken(data);

        // Try to get pool address (use token's pool_address if available, otherwise fetch from GeckoTerminal)
        let poolAddr = data.pool_address;
        if (!poolAddr) {
          poolAddr = await getPoolAddress(tokenAddress);
        }
        setPoolAddress(poolAddr);

        // Fetch pool data if we have a pool address
        if (poolAddr) {
          const poolInfo = await fetchPoolData(poolAddr);
          setPoolData(poolInfo);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error:', err);
        setError('Failed to load token');
        setLoading(false);
      }
    };

    fetchData();
  }, [tokenAddress]);

  // Calculate 24h change from chart data or pool data
  const priceChange24h = chartData && chartData.length >= 2
    ? (() => {
      const firstClose = getValidNumber(chartData[0].close);
      const lastClose = getValidNumber(chartData[chartData.length - 1].close);
      if (firstClose === null || lastClose === null || firstClose === 0) return null;
      const change = ((lastClose - firstClose) / firstClose) * 100;
      return isNaN(change) || !isFinite(change) ? null : change;
    })()
    : getValidNumber(poolData?.price_change_percentage?.h24);

  // Get 24h volume from pool data (more accurate) or calculate from chart data
  const volume24h = poolData?.volume_usd?.h24
    ? (typeof poolData.volume_usd.h24 === 'number' ? poolData.volume_usd.h24 : parseFloat(poolData.volume_usd.h24) || 0)
    : (chartData && chartData.length > 0 ? chartData.reduce((sum: number, candle: CandleData) => sum + (Number(candle.volume) || 0), 0) : 0);

  if (loading) {
    return (
      <TerminalContainer>
        <EthNavBar />
        <LoadingState>Loading token data...</LoadingState>
        <EthPriceFooter />
      </TerminalContainer>
    );
  }

  if (error || !token) {
    return (
      <TerminalContainer>
        <EthNavBar />
        <ErrorState>
          <ErrorMessage>{error || 'Token not found'}</ErrorMessage>
          <BackButton onClick={() => navigate('/eth')}>
            Back to Ethereum Tokens
          </BackButton>
        </ErrorState>
        <EthPriceFooter />
      </TerminalContainer>
    );
  }

  return (
    <TerminalContainer>
      <EthNavBar />

      <CoverBanner>
        <BannerContent>
          <BannerLogo src={token.logo_url || '/default-token.png'} alt={token.name} />
          <BannerInfo>
            <BannerTitle>
              <TokenNameDisplay>{token.name}</TokenNameDisplay>
              <div>
                <TokenSymbolDisplay>${token.symbol}</TokenSymbolDisplay>
                <ChainBadge>on Ethereum</ChainBadge>
              </div>
            </BannerTitle>
            <BannerActions>
              {sanitizeUrl(token.website) && (
                <SocialButton href={sanitizeUrl(token.website)!} target="_blank" rel="noopener noreferrer" title="Website">
                  <Globe size={18} />
                </SocialButton>
              )}
              {sanitizeUrl(token.twitter) && (
                <SocialButton href={sanitizeUrl(token.twitter)!} target="_blank" rel="noopener noreferrer" title="Twitter">
                  <Twitter size={18} />
                </SocialButton>
              )}
              {sanitizeUrl(token.telegram) && (
                <SocialButton href={sanitizeUrl(token.telegram)!} target="_blank" rel="noopener noreferrer" title="Telegram">
                  <Send size={18} />
                </SocialButton>
              )}
              <SocialButton
                href={`${explorerLinkPrefix}/token/${token.contract_address}`}
                target="_blank"
                rel="noopener noreferrer"
                title="View on Etherscan"
              >
                <ExternalLink size={18} />
              </SocialButton>
            </BannerActions>
          </BannerInfo>
        </BannerContent>
      </CoverBanner>

      <TopBar>
        <TopBarContent>
          <TopBarLeft>
            <TokenInfoBar>
              <TokenLogoSmall src={token.logo_url || '/default-token.png'} alt={token.symbol} />
              <TokenPair>
                <TokenSymbolBar>{token.symbol}</TokenSymbolBar>
                <TokenPairLabel>/ ETH</TokenPairLabel>
              </TokenPair>
            </TokenInfoBar>
            <Divider />
            <PriceInfo>
              <PriceValue>${priceUSD ? priceUSD.toFixed(6) : '...'}</PriceValue>
              {priceChange24h !== null && (
                <PriceChange $positive={priceChange24h >= 0}>
                  {priceChange24h >= 0 ? <ArrowUpRight /> : <ArrowDownRight />}
                  {Math.abs(priceChange24h).toFixed(2)}%
                </PriceChange>
              )}
            </PriceInfo>
          </TopBarLeft>

          <TopBarRight>
            <StatsBar>
              <StatItem>
                <StatLabel>MCAP</StatLabel>
                <StatValue>{priceUSD ? formatMarketCap(priceUSD) : '...'}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>VOL 24H</StatLabel>
                <StatValue>${volume24h.toLocaleString(undefined, { maximumFractionDigits: 0 })}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>LIQUIDITY</StatLabel>
                <StatValue>{poolData?.reserve_in_usd ? `$${parseFloat(poolData.reserve_in_usd).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '...'}</StatValue>
              </StatItem>
            </StatsBar>
          </TopBarRight>
        </TopBarContent>
      </TopBar>

      <MainContent>
        <LeftSidebar>
          <TerminalCard>
            <CardHeader>
              <CardTitle>Token Info</CardTitle>
              <Activity size={14} color="rgba(236, 240, 241, 0.5)" />
            </CardHeader>
            <CardContent>
              <PriceDisplay>
                <PriceLabel>Price USD</PriceLabel>
                <PriceAmount>${priceUSD ? priceUSD.toFixed(8) : '...'}</PriceAmount>
              </PriceDisplay>

              <DividerLine />

              <StatRow>
                <StatRowLabel>Market Cap</StatRowLabel>
                <StatRowValue $highlight>{priceUSD ? formatMarketCap(priceUSD) : '...'}</StatRowValue>
              </StatRow>
              <StatRow>
                <StatRowLabel>Liquidity</StatRowLabel>
                <StatRowValue>{poolData?.reserve_in_usd ? `$${parseFloat(poolData.reserve_in_usd).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '...'}</StatRowValue>
              </StatRow>
              <StatRow>
                <StatRowLabel>FDV</StatRowLabel>
                <StatRowValue>{poolData?.fdv_usd ? `$${parseFloat(poolData.fdv_usd).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '...'}</StatRowValue>
              </StatRow>

              <DividerLine />

              <StatRow>
                <StatRowLabel>24h Volume</StatRowLabel>
                <StatRowValue>${volume24h.toLocaleString(undefined, { maximumFractionDigits: 0 })}</StatRowValue>
              </StatRow>
              <StatRow>
                <StatRowLabel>24h Change</StatRowLabel>
                <StatRowValue style={{ color: (priceChange24h || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                  {(priceChange24h || 0) >= 0 ? '+' : ''}{(priceChange24h || 0).toFixed(2)}%
                </StatRowValue>
              </StatRow>

              <DividerLine />

              <ContractDisplay>
                <PriceLabel>Contract Address</PriceLabel>
                <ContractAddress onClick={() => {
                  navigator.clipboard.writeText(token.contract_address);
                  alert('Address copied!');
                }}>
                  <ContractText>{token.contract_address}</ContractText>
                  <IconButton title="Copy Address">
                    <Copy size={12} />
                  </IconButton>
                </ContractAddress>
              </ContractDisplay>

              <SocialLinksRow>
                {sanitizeUrl(token.website) && (
                  <SocialLinkSmall href={sanitizeUrl(token.website)!} target="_blank" rel="noopener noreferrer" title="Website">
                    <Globe size={14} />
                  </SocialLinkSmall>
                )}
                {sanitizeUrl(token.twitter) && (
                  <SocialLinkSmall href={sanitizeUrl(token.twitter)!} target="_blank" rel="noopener noreferrer" title="Twitter">
                    <Twitter size={14} />
                  </SocialLinkSmall>
                )}
                {sanitizeUrl(token.telegram) && (
                  <SocialLinkSmall href={sanitizeUrl(token.telegram)!} target="_blank" rel="noopener noreferrer" title="Telegram">
                    <Send size={14} />
                  </SocialLinkSmall>
                )}
                <SocialLinkSmall href={`${explorerLinkPrefix}/token/${token.contract_address}`} target="_blank" rel="noopener noreferrer" title="Etherscan">
                  <ExternalLink size={14} />
                </SocialLinkSmall>
              </SocialLinksRow>
            </CardContent>
          </TerminalCard>

          <AboutCard>
            <CardHeader>
              <CardTitle>About {token.symbol}</CardTitle>
              <Shield size={14} color="rgba(236, 240, 241, 0.5)" />
            </CardHeader>
            <CardContent>
              <AboutText>
                {token.description || `No description available for ${token.name}.`}
              </AboutText>
              <AuditBadge>
                <Shield size={12} />
                <span>Contract Verified</span>
              </AuditBadge>
            </CardContent>
          </AboutCard>
        </LeftSidebar>

        <CenterSection>
          <ChartSection>
            <TokenChart
              tokenAddress={token.contract_address}
              tokenSymbol={token.symbol}
              poolAddress={poolAddress || undefined}
              network={ETH_NETWORK}
            />
          </ChartSection>

          <BottomSection>
            <TokenComments
              tokenAddress={token.contract_address}
            />
          </BottomSection>
        </CenterSection>

        <RightSidebar>
          <SwapWidgetContainer>
            <CustomSwapWidget
              preselectedToToken={{
                address: token.contract_address,
                symbol: token.symbol,
                name: token.name,
                chainId: 1,
                logo: token.logo_url
              }}
              embedded={true}
            />
          </SwapWidgetContainer>
        </RightSidebar>
      </MainContent>

      <EthPriceFooter />
    </TerminalContainer>
  );
};

export default EthTokenDetailPage;
